<template>
  <article
    class="play-canvas"
    id="play-canvas"
    :class="{
      expanded: $store.state.optionsCollapsed,
      'on-form-screen': $route.path === '/summary',
    }"
  >
    <div id="visualizer" class="visualizer">
      <!-- Canvas container crops canvas edges -->
      <div class="canvas-container">
        <canvas id="application-canvas"></canvas>
      </div>
      <!-- Back to main site button -->
      <a class="logo" :href="rtsData.url" target="_blank" >
        <img v-if="getT" src="../assets/images/logo-white.svg" v-bind:alt="getT.static.back_to_main_site" />
      </a>
      <!-- Any errors with trying to use WebGL-->
      <div class="webgl-error" id="webgl-error"></div>
      <!-- Back button -->
      <button class="back" v-on:click="goBack()">
        <h3 :class="{ hideForLoading: showLoading }">
          {{ this.currPool.title }}
        </h3>
        <img src="../assets/icons/right-arrow-small.svg" />
        <span v-if="getT" :class="{ hideForLoading: showLoading }">
          {{
            $route.path === '/summary'
              ? getT.static.summary[0].back_button_text
              : getT.static.customizer[0].back_to_select
          }}</span
        >
      </button>
      <!-- Spin notifier -->
      <div class="spin-wrap" :class="{ hide: hideSpin || showLoading }">
        <p class="spin" aria-hidden>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12.688"
            height="6.344"
            viewBox="0 0 12.688 6.344"
          >
            <g transform="translate(0 0)">
              <path
                class="arrow-left"
                d="M275.21,408.941l-.11-1.684c-.072,0-6.463.338-9.839-2.951h3.3v-1.687h-6.039v5.905h1.725v-2.811c3.215,2.941,8.245,3.249,10.18,3.249C274.87,408.961,275.151,408.944,275.21,408.941Z"
                transform="translate(-262.522 -402.617)"
              />
            </g>
          </svg>
          <span v-if="getT">
            {{ getT.static.play_canvas[0].spin_button_text }}</span
          >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12.688"
            height="6.344"
            viewBox="0 0 12.688 6.344"
          >
            <g transform="translate(0 0)">
              <path
                class="arrow-right"
                d="M262.522,408.941l.11-1.684c.072,0,6.463.338,9.839-2.951h-3.3v-1.687h6.039v5.905h-1.725v-2.811c-3.215,2.941-8.245,3.249-10.18,3.249C262.862,408.961,262.581,408.944,262.522,408.941Z"
                transform="translate(-262.522 -402.617)"
              />
            </g>
          </svg>
        </p>
      </div>
      <!-- Water toggle -->
      <button
        class="toggle-water"
        :class="{ active: waterToggled, hideForLoading: showLoading }"
        v-on:click="toggleWater()"
        aria-label="Toggle water visuals"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="14"
          viewBox="0 0 10 14"
        >
          <path
            class="droplet"
            d="M.469-12.781q.625.656,1.25,1.438t1.453,2A11.145,11.145,0,0,1,4.5-6.656,8.89,8.89,0,0,1,5-3.781,4.492,4.492,0,0,1,3.531-.391,4.954,4.954,0,0,1,0,1,5.161,5.161,0,0,1-2.422.422,4.625,4.625,0,0,1-4.187-1.156a7.349,7.349,0,0,1-.469-.906A4.518,4.518,0,0,1-5-3.781v-.125A11.087,11.087,0,0,1-3-9.625a24.339,24.339,0,0,1,1.844-2.406q.563-.625.688-.75l.031-.031A.771.771,0,0,1,0-13a.771.771,0,0,1,.438.188ZM0-.75a3.007,3.007,0,0,0,2.2-.922,3.007,3.007,0,0,0,.922-2.2,3.189,3.189,0,0,0-.25-1.25A5.579,5.579,0,0,1,1.313-2.484,5.364,5.364,0,0,1-1.375-1.062,3.3,3.3,0,0,0,0-.75Z"
            transform="translate(5 13)"
          />
        </svg>
        <span v-if="getT">
          {{ getT.static.play_canvas[0].water_button_text }}</span
        >
      </button>
      <!-- Overlay - model/textures are loading -->
      <div class="loading-model" id="loading-model" v-if="showLoading">
        <div class="content">
          <!-- SVG loading icon by Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL  -->
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#fff"
          >
            <g fill="none" fill-rule="evenodd" stroke-width="2">
              <circle cx="22" cy="22" r="1">
                <animate
                  attributeName="r"
                  begin="0s"
                  dur="1.8s"
                  values="1; 20"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.165, 0.84, 0.44, 1"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  begin="0s"
                  dur="1.8s"
                  values="1; 0"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.3, 0.61, 0.355, 1"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="22" cy="22" r="1">
                <animate
                  attributeName="r"
                  begin="-0.9s"
                  dur="1.8s"
                  values="1; 20"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.165, 0.84, 0.44, 1"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  begin="-0.9s"
                  dur="1.8s"
                  values="1; 0"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.3, 0.61, 0.355, 1"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          </svg>
          <header>
            <h2 v-if="getT">{{ getT.static.play_canvas[0].loading_text }}</h2>
          </header>
        </div>
      </div>
    </div>
  </article>
</template>
<script>
import PlayCanvas from '../playcanvas';
import { mapGetters } from 'vuex';
import { breakpoint } from '../styles/style';

export default {
  name: 'PlayCanvas',
  data: function () {
    return {
      waterToggled: true,
      showLoading: true,
      firstLoad: true,
      canvas: null,
      hideSpin: false,
      ledColor: '#cfcfcf',
      canvasWidth: window.innerWidth,
      canvasHeight: window.innerHeight,
    };
  },
  computed: {
    ...mapGetters(['rtsData', 'getT', 'currPool']),
  },
  mounted: function () {
    this.canvas = document.querySelector('canvas#application-canvas');
    
    /*
      I tried to remove these duplicative event listeners and replace them with a 
      watch: option that watches the vuex store's isLoading getter through mapGetters 
      and it seemed exactly the same except for a slight blink in the 3D view when 
      the state changed. 

      So I'm guessing the duplicative event listeners are here so we avoid
      Vue stuff disturbing this element that holds the PlayCanvas app. 
    */
    document.addEventListener('show-loading', () => {
      this.showLoading = true;

      this.canvas.classList.add('blur');
    });
    document.addEventListener('hide-loading', () => {
      this.canvas.classList.remove('blur');
      document.querySelector('#loading-model')?.classList.add('fade-out');

      this.fitCanvas();

      // Allow blur + opacity animation to run before hide loading element
      setTimeout(() => {
        this.showLoading = false;
      }, 950);
    });

    // Hide spin text on user interact with canvas
    const hideSpin = () => {
      this.hideSpin = true;
      // Only run once
      this.canvas.removeEventListener('mousedown', hideSpin);
      this.canvas.removeEventListener('touchstart', hideSpin);
    };
    this.canvas.addEventListener('mousedown', hideSpin);
    this.canvas.addEventListener('touchstart', hideSpin);

    // Observe canvas for resizing
    // https://caniuse.com/#feat=resizeobserver
    let lastUpdate = -1;
    let interval = 400;

    // Can't update too often or screen goes black, update only every 400ms
    // this setTimeout makes sure the final dimensions are set
    let delayedResize = null;
    window.addEventListener('resize', () => {
      let now = Date.now();
      if (now - lastUpdate > interval) {
        lastUpdate = now;
        // New update, can throw away setTimeout
        delayedResize = null;
        this.fitCanvas();
        // request render to fix aspect ratio. need delay due to transition
        setTimeout(() => {
          if (window.RenderCenter) window.RenderCenter.renderRequested = true;
        }, 450);
      } else if (delayedResize == null) {
        // After interval + 50ms if a new dimension has not been set, do one FINAL update
        delayedResize = setTimeout(() => {
          //this.updateCanvasDimensions(); // canvas.clientWidth, canvas.clientHeight);
          this.fitCanvas();
        }, interval + 50);
      }
    });

    // Fit canvas based on screen dimensions
    this.fitCanvas();
  },
  methods: {
    fitCanvas: function () {
      // Screen sizing
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = width / height;

      if (width >= breakpoint.xl) {
        // Desktop
        this.setCanvasFitDirection('width');

        this.canvasWidth = width - 384;
        this.canvasHeight = height - 50;
      } else if (width >= breakpoint.md) {
        // iPad / weird desktop window sizes
        // Either fit the width or the height depending on whats larger
        if (width > height - 110) {
          // Fit width
          this.setCanvasFitDirection('width');
          this.canvasWidth = width;
          this.canvasHeight = height - 110;
        } else {
          // Fit height
          this.setCanvasFitDirection('height');
          this.canvasHeight = height - 110;
          this.canvasWidth = width * 2;
        }
      } else {
        // Mobile
        if (height > width) {
          // Portrait orientation
          this.setCanvasFitDirection('height');

          // Set canvas resolution (1:1)
          this.canvasHeight = height * 0.462 - 50; // 46.2vh
          this.canvasWidth = Math.round(
            this.canvasHeight * Math.pow(aspectRatio + 0.8, window.power)
          );
          // calc((46vh - 50px)*Math.pow(.68,2.6))
        } else {
          // Landscape orientation
          this.setCanvasFitDirection('width');
          this.canvasHeight = window.innerHeight - 110;
          this.canvasWidth = window.innerWidth;
        }
      }

      this.syncCanvasDimensions();
    },
    setCanvasFitDirection: function (direction) {
      const otherDirection = direction === 'width' ? 'height' : 'width';

      if (this.canvas.classList.contains(`fit-${otherDirection}`)) {
        this.canvas.classList.remove(`fit-${otherDirection}`);
      }
      this.canvas.classList.add(`fit-${direction}`);
    },
    syncCanvasDimensions: function () {
      const sync = () => {
        if (window?.pc?.app?.graphicsDevice) {
          // Tell PlayCanvas to update canvas sizing
          window.pc.app.graphicsDevice.resizeCanvas(
            this.canvasWidth,
            this.canvasHeight
          );

          // Push new frame
          if (window.RenderCenter) {
            window.RenderCenter.renderRequested = true;
          }
        } else {
          setTimeout(sync, 500);
        }
      };

      sync();
    },
    toggleWater: function () {
      this.waterToggled = !this.waterToggled;
      window.pc.Application.getApplication().fire('water:toggle');
    },
    goBack: function () {
      if (this.$router.currentRoute.value.path === '/summary') {
        this.$router.push('/customizer');
        
        // track event for Google Analytics
        this.$gtag.event('Edit Design clicked', {
          'event_category': 'Edit Design',
          'event_label': 'Edit Design clicked',
          'selected_pool': this.currPool.title
        })
      } else {
        this.$router.push('/select-pool');
        
        // track event for Google Analytics
        this.$gtag.event('Change Model clicked', {
          'event_category': 'Model Change',
          'event_label': 'Change Model clicked',
          'selected_pool': this.currPool.title
        });
      }
    },
    setAccessoryColor: function (evt) {
      if (evt.target) {
        const colorHex = evt.target.getAttribute('data-color');
        const bgCSS = evt.target.style.background;
        const currentColorElem = document.querySelector('#current-led-color');

        // Update UI to show different color selected
        this.ledColor = colorHex;
        currentColorElem.style.background = bgCSS;

        if (colorHex) {
          PlayCanvas.setAccessoryColor('led-lighting', colorHex);
        }
      }
    },
    toggleLights: function () {
      PlayCanvas.toggleLEDLighting();
    },
  }
};
</script>
<style lang="scss">
.hideForLoading {
  display: none !important;
}
article.play-canvas {
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(55% - 4em);
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-justify-content: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
  background-color: lightgrey;
  transition: all 400ms cubic-bezier(0.645, 0.045, 0.355, 1);
  .canvas-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    overflow: hidden;
    canvas#application-canvas {
      position: relative !important;
      //position: absolute;
      //width: 100%;
      //height: 100%;
      &.fit-width {
        height: auto !important;
        min-height: 100% !important;
        width: 100% !important;
        max-width: 100%;
      }
      &.fit-height {
        width: auto !important;
        min-width: 100% !important;
        height: 100% !important;
        max-height: 100%;
      }
    }
  }
  .webgl-error {
    position: absolute;
    z-index: 100;
    display: none;
    width: 100%;
    max-width: 30em;
    padding: 2em 3em;
    font-size: 1rem;
    font-weight: bold;
    background-color: rgb(129, 25, 25);
    color: white;
    &.show {
      display: block;
    }
    a {
      color: #f98181;
      cursor: pointer;
      &:hover {
        color: #ff4646;
      }
    }
  }
  &.expanded {
    height: calc(100% - 7em);
  }
  &.on-form-screen {
    top: 6rem;
    left: 1.5em;
    width: calc(100% - 3em);
    height: 18em;
    z-index: 50;
    box-shadow: 0 0 1.5em rgb(9 20 37 / 0.7);
    button.back {
      bottom: unset;
      top: 0;
    }
    button.toggle-water,
    .spin-wrap,
    a.logo {
      display: none;
    }
  }
  .config {
    position: absolute;
    width: 100%;
    height: 100%;
    padding: 3em;
    div {
      height: 13px;
    }
  }
  .visualizer {
    position: relative;
    width: 100%;
    max-width: 100vw;
    height: 100%;
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-justify-content: center;
    justify-content: center;
    background-color: $deep-navy;
  }
  button.back,
  button.toggle-water,
  a.logo {
    text-align: left;
    z-index: 1;
    padding: 0;
    letter-spacing: 0;
    background: transparent;
    border: none;
    transition: none;
    user-select: none;
    font-family: 'Europa Regular';
    margin: 1.7em 2em;
    font-size: 1rem;
    span {
      letter-spacing: 0.1em;
    }
    @media (max-width: $xxxl) {
      margin: 1.4em 1.7em;
    }
    @media (max-width: $xxl) {
      margin: 1em 1.2em;
    }
    @media (max-width: $sm) {
      font-size: 1rem;
    }
  }
  a.logo {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    a {
      svg .a {
        fill: #fff;
      }
      @media #{$desktop} {
        &:not([disabled]) {
          cursor: pointer !important; // To avoid blue highlight on chrome mobile
        }
      }
    }
  }
  button.back {
    position: absolute;
    left: 0;
    bottom: 0;
    h3 {
      color: #fff;
      font-family: 'Austin Bold', serif;
      font-weight: unset;
      letter-spacing: 0.8px;
      font-size: 1.625rem;
      margin-bottom: 0.1em;
      text-transform: none;
    }
    img {
      transform: rotate(180deg);
      margin-bottom: -0.1em;
    }
  }
  button.toggle-water {
    position: absolute;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    svg {
      min-width: 1rem;
      height: auto;
      margin-bottom: 0.3em;
      transition: none;
      .droplet {
        fill: #fff;
      }
    }
  }
  .spin-wrap {
    position: absolute;
    z-index: 1;
    left: 0;
    bottom: 5em;
    padding: 0.5em;
    width: 100%;
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-justify-content: center;
    justify-content: center;
    pointer-events: none;
    transition: transform 500ms ease-in, opacity 500ms ease-in;
    &.hide {
      transition: transform 500ms ease-in, opacity 500ms ease-in;
      transform: translateY(2em);
      opacity: 0;
    }
    .spin {
      color: white;
      text-transform: uppercase;
      font-size: 1.2rem;
      letter-spacing: 0;
      user-select: none;
      svg {
        width: auto;
        height: 0.5em;
        margin: 0 0.2em 0.2em 0.2em;
        .arrow-left,
        .arrow-right {
          fill: #fff;
        }
      }
    }
    span {
      font-family: 'Europa Bold';
      font-weight: unset;
    }
    @media only screen and (min-width: $xxl) {
      bottom: 3em;
    }
  }
  @media only screen and (min-width: #{ $sm-md }) and (max-width: #{ $xl }) and (max-height: 900px) {
    min-height: $min-3d-viewport;
    .visualizer {
      width: 100%;
      height: 100%;
    }
  }
  @media #{$desktop} {
    width: calc(100% - #{$aside-desktop-width});
    min-width: calc(100% - #{$max-aside-desktop-width});
    max-width: calc(100% - #{$min-aside-desktop-width});
    height: 100%;
    overflow: hidden;
    // Parent component has great cascading, but need to override on desktop
    // Using ID to get greater cascade (vs !important everywhere)
    &.on-form-screen {
      left: 2em;
      width: calc(100% - 33em);
      max-width: calc(70% - 3em);
      height: calc(100% - 10em);
      #application-canvas {
        width: 100%;
        height: 100%;
      }
    }
  }
  // Loading overlay when models switched + scene loading
  .loading-model {
    position: absolute;
    z-index: 9;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-justify-content: center;
    justify-content: center;
    align-items: center;
    // background-color: rgba(0, 0, 0, 0.64);
  }
  .loading-model .content {
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    flex-direction: column;
    -webkit-justify-content: center;
    justify-content: center;
    align-items: center;
  }
  .loading-model svg {
    width: 3em;
    height: 3em;
    @media #{$desktop} {
      margin: 1em;
      width: 5em;
      height: 5em;
    }
  }
  .loading-model header h2 {
    margin: 1em;
    color: #fff;
    text-align: center;
    letter-spacing: 2.24px;
    color: #fff;
    text-transform: uppercase;
    max-width: 10em;
    font-size: 1.5rem;
    animation: fade-in 1500ms;
    @keyframes fade-in {
      0% {
        opacity: 0;
      }
      50% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
    @media #{$desktop} {
      max-width: unset;
      font-size: 2.25rem;
    }
  }
  // Fade out animation when model is done loading
  .loading-model.fade-out {
    animation: fade-out 1000ms;
  }
  @keyframes fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  // Canvas blurring
  canvas {
    transition: filter 1000ms;
  }
  canvas.blur {
    transition-duration: 0ms;
    filter: blur(6px) brightness(80%);
  }
  // Make more room for options on phones/tablets in horizontal orientation
  @media only screen and (max-width: 1020px) and (min-aspect-ratio: 5/4) {
    &.expanded {
      max-height: calc(100% - 7.3em);
      min-height: calc(100% - 7.3em);
    }
    &:not(.expanded) {
      max-height: 0;
      min-height: 0;
    }
  }
}
</style>
